import { datadogRum } from '@datadog/browser-rum';
import {
  type CreateAccountRequest,
  type CreateAccountResponse,
  type GetAccountHostingPlanResponse,
  type GetAccountMeResponse,
  type ProductPricing,
  RocketAPI,
  type SiteTaskWithSiteInfo,
} from '@portal/rocket-api-client';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { UserRole } from 'api/accountUsers';
import { AxiosService } from 'api/axiosService';

export type Profile = NonNullable<GetAccountMeResponse['result']>;
export type Task = NonNullable<Profile['tasks']>[number];
export type TaskWithSiteInfo = NonNullable<SiteTaskWithSiteInfo>;

export type Metric = keyof NonNullable<NonNullable<GetAccountMeResponse['result']>['usage']>;

export type BillingAddon = NonNullable<
  NonNullable<GetAccountHostingPlanResponse['result']>['addons']
>[0];

export type BillingCurrentPlan = NonNullable<
  NonNullable<GetAccountHostingPlanResponse['result']>['current_plan']
>;

export type BillingProductCycle = ProductPricing['billing_cycle'];

const TASK_POLLING_INTERVAL = 1000;

export const profileQueryOptions = () => ({
  ...RocketAPI.account.getFindAccountMeQueryOptions(),
  queryFn: queryFnUseProfile,
  gcTime: 10 * 1000,
  staleTime: 10 * 1000,
  retry: false as const,
});

export const getProfileRoles = (profileData?: GetAccountMeResponse): UserRole[] => {
  // TODO: Remove `as UserRole[]` when the no account role is added to openapi types
  return (profileData?.result?.client?.roles ?? []) as UserRole[];
};

let datadogRumInitialized = false;

const queryFnUseProfile = async () => {
  const response = await RocketAPI.account.findAccountMe();

  const usage = response.result?.usage;
  const limits = response.result?.limits;

  if (usage && limits) {
    usage.disk = Math.ceil(Number(usage.disk));
    usage.bandwidth = Math.ceil(Number(usage.bandwidth));
    limits.disk = Math.ceil(Number(limits.disk) * 1024);
    limits.bandwidth = Math.ceil(Number(limits.bandwidth) * 1024);
  }

  if (!datadogRumInitialized) {
    datadogRum.setUser({
      id: response.result?.client?.id ?? '',
    });
    datadogRumInitialized = true;
  }

  return response;
};

export const useProfile = (enabled = true) =>
  useQuery({
    ...profileQueryOptions(),
    enabled,
  });

export const useProfileSuspense = () => useSuspenseQuery(profileQueryOptions());

export const useProfileTaskPolling = () =>
  useQuery({
    ...profileQueryOptions(),
    refetchInterval: query => {
      const hasInProgressTasks = query.state.data?.result?.tasks?.some(
        task => task.task_status === 'PROGRESS' || task.task_status === 'NEW'
      );
      return hasInProgressTasks ? TASK_POLLING_INTERVAL : false;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    retry: false,
  });

// TODO: Use the generated hook when internal endpoints are included.
export const useCreateAccount = () => {
  return useMutation({
    mutationFn: async (data: CreateAccountRequest) => {
      return await AxiosService.post<CreateAccountResponse>('/accounts', data);
    },
  });
};

// Using a custom mutation, the generated one cannot pass the required scoped token.
export const useDisableTwoFactorWithScopedToken = () => {
  const { mutationKey, meta } = RocketAPI.account.getModifyAccountMeMutationOptions();

  return useMutation({
    mutationKey,
    meta,
    mutationFn: ({ token }: { token: string }) =>
      AxiosService.patch<GetAccountMeResponse>(
        '/account/me',
        { twofactor_enabled: false },
        { headers: { Authorization: `Bearer ${token}` } }
      ),
  });
};

export const useGetAccountVisitors = (startDate: Date, endDate: Date) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');

  return RocketAPI.visitors.useListAccountVisitorsSuspense(
    { date_from: formattedStartDate, date_to: formattedEndDate },
    { query: { staleTime: 5 * 60_000 } }
  );
};
